import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  toggleTocMenuIsClosed,
  setCurrentPageId,
  reset,
  setActiveSectionId,
  initializeCourseStructure,
  updateCourseProgress
} from '../viewCourseState';

// Action hooks for viewCourse
export const useViewCourseActions = () => {
  const dispatch = useDispatch();

  return {
    toggleTocMenuIsClosed: useCallback(() => {
      dispatch(toggleTocMenuIsClosed());
    }, [dispatch]),

    setCurrentPageId: useCallback((pageId) => {
      dispatch(setCurrentPageId(pageId));
    }, [dispatch]),

    reset: useCallback(() => {
      dispatch(reset());
      // Note: Not resetting RTKQ cache to preserve data
    }, [dispatch]),

    setActiveSectionId: useCallback((sectionId) => {
      dispatch(setActiveSectionId(sectionId));
    }, [dispatch]),

    initializeCourseStructure: useCallback((course) => {
      dispatch(initializeCourseStructure(course));
    }, [dispatch]),

    updateCourseProgress: useCallback((progressData) => {
      dispatch(updateCourseProgress(progressData));
    }, [dispatch]),
  };
};

// Individual action hooks
export const useToggleTocMenuIsClosed = () => {
  const { toggleTocMenuIsClosed } = useViewCourseActions();
  return toggleTocMenuIsClosed;
};

export const useSetCurrentPageId = () => {
  const { setCurrentPageId } = useViewCourseActions();
  return setCurrentPageId;
};

export const useResetViewCourse = () => {
  const { reset } = useViewCourseActions();
  return reset;
};

export const useSetActiveSectionId = () => {
  const { setActiveSectionId } = useViewCourseActions();
  return setActiveSectionId;
};

export const useInitializeCourseStructure = () => {
  const { initializeCourseStructure } = useViewCourseActions();
  return initializeCourseStructure;
};

export const useUpdateCourseProgress = () => {
  const { updateCourseProgress } = useViewCourseActions();
  return updateCourseProgress;
};

// Utility hook for complete course view reset (including cache)
export const useResetViewCourseCompletely = () => {
  const dispatch = useDispatch();

  return useCallback((reason = 'manual') => {
    // Reset state
    dispatch(reset());

  }, [dispatch]);
};

// Smart cache invalidation for course switching
export const useInvalidateCourseCache = () => {
  const dispatch = useDispatch();

  return useCallback((previousCourseId, newCourseId) => {
    if (previousCourseId && previousCourseId !== newCourseId) {
      // Only clear page state, let RTKQ handle cache naturally
      // This prevents stale content without causing double fetches
      dispatch({ type: 'coursePage/clearPage' });
    }
  }, [dispatch]);
};
